import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import styled from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import ScrollableTable from '@/components/ScrollableTable';
import { Table, TableContent, Td, Th } from '@/components/Table';
import Text from '@/components/Text';
import { EditorialFacebookPostResource, FBPostAd } from '@/types/resources';
import { api } from '@/utils/api';

import NewAdForm from './NewAdForm';

const Dialog = styled(StyledDialog)`
  width: 1160px;
  text-align: left;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  height: 72px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e7e7e7;
  box-shadow: 0px 1px 0px 0px #22222214;
`;

const Time = styled(Text)`
  margin-top: 4px;
`;

const Body = styled.div`
  padding: 24px 24px 0;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  height: 614px;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.div`
  border-radius: 0 0 4px 4px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscale};
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: flex-end;
  height: 112px;
  padding: 0 24px;
  box-shadow: 0px -1px 8px 0px #2222220f;
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const StyledTable = styled(Table)`
  th,
  td {
    &:first-child {
      padding-left: 24px;
    }
    &:last-child {
      padding-right: 24px;
    }
  }
`;

const formatCurreny = (value: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    value,
  );

const formatLocations = (value: Record<string, string[]>) => {
  return Object.keys(value).reduce<string[]>((acc, key) => {
    return [...acc, ...value[key]];
  }, []);
};

interface Props {
  onClose: VoidFunction;
  post: EditorialFacebookPostResource;
}

const AdsModal: FC<Props> = ({ onClose, post }) => {
  const [showForm, setShowForm] = useState(false);
  const {
    data: ads,
    isLoading,
    isError,
  } = useQuery(
    [
      'fb_post_ads',
      {
        postId: post.id,
      },
    ],
    () =>
      api.get<never, FBPostAd[]>('/v1/editorial/facebook-post-ads', {
        params: {
          'filter[post_id]': post.id,
        },
      }),
  );

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <Header>
          <Text $type="button">Post Facebook Ads</Text>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </Header>
        <Body>
          <ScrollableTable>
            <StyledTable>
              <thead>
                <tr>
                  <Th>Created At</Th>
                  <Th>Objective</Th>
                  <Th>Start Date</Th>
                  <Th>End Date</Th>
                  <Th>Stats Updated At</Th>
                  <Th>Daily Budget</Th>
                  <Th>Bid Amount</Th>
                  <Th>Locations</Th>
                  {/* Metrics and info: */}
                  <Th>Status</Th>
                  <Th>Impressions</Th>
                  <Th>Link Clicks</Th>
                  <Th>CTR</Th>
                  <Th>Reactions</Th>
                  <Th>Comments</Th>
                  <Th>Page Likes</Th>
                  <Th>CPC</Th>
                  <Th>Total Cost</Th>
                </tr>
              </thead>
              <tbody>
                <TableContent
                  isLoading={isLoading}
                  isError={isError}
                  hasData={!!ads?.length}
                >
                  {ads?.map((ad) => (
                    <tr key={ad.id}>
                      <Td>
                        {DateTime.fromISO(ad.created_at).toLocaleString()}
                        <br />
                        <Time $color="gray" $type="extraSmall">
                          {DateTime.fromISO(ad.created_at).toFormat('HH:mm')}
                        </Time>
                      </Td>
                      <Td>{ad.adset_data.optimization_goal}</Td>
                      <Td>
                        {DateTime.fromISO(
                          ad.adset_data.start_time,
                        ).toLocaleString()}
                        <br />
                        <Time $color="gray" $type="extraSmall">
                          {DateTime.fromISO(ad.adset_data.start_time).toFormat(
                            'HH:mm',
                          )}
                        </Time>
                      </Td>
                      <Td>
                        {DateTime.fromISO(
                          ad.adset_data.end_time,
                        ).toLocaleString()}
                        <br />
                        <Time $color="gray" $type="extraSmall">
                          {DateTime.fromISO(ad.adset_data.end_time).toFormat(
                            'HH:mm',
                          )}
                        </Time>
                      </Td>
                      <Td>
                        {ad.stats_updated_at ? (
                          <>
                            {DateTime.fromISO(
                              ad.stats_updated_at,
                            ).toLocaleString()}
                            <br />
                            <Time $color="gray" $type="extraSmall">
                              {DateTime.fromISO(ad.stats_updated_at).toFormat(
                                'HH:mm',
                              )}
                            </Time>
                          </>
                        ) : (
                          '-'
                        )}
                      </Td>
                      <Td>{formatCurreny(ad.adset_data.daily_budget / 100)}</Td>
                      <Td>{formatCurreny(ad.adset_data.bid_amount / 100)}</Td>
                      <Td>
                        {formatLocations(ad.adset_data.targeting.geo_locations)}
                      </Td>
                      <Td>{ad.status}</Td>
                      <Td>{ad.metrics?.impressions ?? 0}</Td>
                      <Td>{ad.metrics?.link_clicks ?? 0}</Td>
                      <Td>{ad.metrics?.ctr ?? 0}</Td>
                      <Td>{ad.metrics?.reactions ?? 0}</Td>
                      <Td>{ad.metrics?.comments ?? 0}</Td>
                      <Td>{ad.metrics?.page_likes ?? 0}</Td>
                      <Td>{ad.metrics?.cpc ?? 0}</Td>
                      <Td>{formatCurreny(ad.metrics?.spend ?? 0)}</Td>
                    </tr>
                  ))}
                </TableContent>
              </tbody>
            </StyledTable>
          </ScrollableTable>
        </Body>
        <Footer>
          <Button type="button" onClick={() => setShowForm(true)}>
            New Ad
          </Button>
          <Button type="button" color="secondary" onClick={onClose}>
            Close
          </Button>
        </Footer>
      </Dialog>
      {showForm && <NewAdForm post={post} onClose={() => setShowForm(false)} />}
    </Modal>
  );
};

export default AdsModal;
